import { useKaraokeStore, useUserIdentity } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import GridIcon from '@src/svgs/GridIcon';
import { observer } from 'mobx-react-lite';
import { type FC } from 'react';
import styled, { css } from 'styled-components';

import { KARAOKE_TRACK_NUMBER } from '../../stores/VideoCreatorStore';
import AiProducerIcon from '../../svgs/AiProducerIcon';
import CaptionsBubbleIcon from '../../svgs/CaptionsBubbleIcon';
import ChevronDownIcon from '../../svgs/ChevronDownIcon';
import MediaIcon from '../../svgs/MediaIcon';
import SpeakersIcon from '../../svgs/SpeakersIcon';
import {
  AIProducerCard,
  DesignCard,
  KaraokeCard,
  SidebarOption,
} from '../../types.ts/general';
import { useFlagsCombination } from '../../utility/useFlagsCombination';
import { HistoryIcon, TextIcon } from '../common/icons';
import { ScrollEdgeFade, useScrollEdges } from '../common/scrollEdgeFade';

/** Matches the `gap` between tabs, so one click lands cleanly on the next tab. */
const RAIL_TAB_GAP = 8;
/** Only used before the first tab has laid out. */
const RAIL_SCROLL_FALLBACK = 72;

type SidebarIconMenuProps = {
  isExpanded: boolean;
  onToggleExpand: () => void;
};

const SidebarIconMenu: FC<SidebarIconMenuProps> = observer(
  ({ isExpanded, onToggleExpand }) => {
    const videoCreator = useVideoCreatorStore();
    const karaokeStore = useKaraokeStore();
    const userIdentity = useUserIdentity();

    const {
      creatorStudioAiEnableTab,
      creatorStudioEnableDesignTabs,
      creatorStudioEnableVideoTemplatesTab,
      creatorStudioAiProducerEnableClipProducerOnly,
      creatorStudioMediaEnableTab,
    } = useFlagsCombination(userIdentity.currentRole);
    const { sidebarOptions } = videoCreator;

    const isProducer = sidebarOptions === SidebarOption.aiProducer;
    const isVideoTemplate = sidebarOptions === SidebarOption.videoTemplate;
    // Media / Captions / Graphics route from timeline element selection, so
    // their rail icon turns custard whenever that option is active — even while
    // the panel is collapsed (PLAT-10391) — to show the element's settings are
    // active but hidden. Other tabs only highlight while the panel is expanded.
    const isMedia = sidebarOptions === SidebarOption.media;
    const isHistory = sidebarOptions === SidebarOption.history;
    const isKaraoke = sidebarOptions === SidebarOption.karaoke;
    const isDesign = sidebarOptions === SidebarOption.design;
    const isSpeakers = sidebarOptions === SidebarOption.speakers;
    const activeElement = videoCreator.getActiveElement();

    const isVideoReady = videoCreator.isVideoCreatorReady;

    // The rail is as tall as the editor area, so on a short viewport (small
    // laptop, un-maximized window, expanded timeline) the last tabs fall past
    // the fold. Scroll them into reach and fade the edge that has more behind
    // it, so a clipped rail no longer looks like the whole list (PLAT-11276).
    const {
      ref: railRef,
      edges: railEdges,
      syncScrollEdges,
    } = useScrollEdges<HTMLDivElement>();

    // Nudge the rail by one tab. The fade alone reads as "the list ends here",
    // so the chevron is what says the rail scrolls — and clicking it beats
    // asking for a wheel gesture on a 64px column.
    const scrollRailBy = (direction: 1 | -1) => {
      const rail = railRef.current;
      if (!rail) return;
      const tab = rail.firstElementChild;
      const step =
        tab instanceof HTMLElement
          ? tab.offsetHeight + RAIL_TAB_GAP
          : RAIL_SCROLL_FALLBACK;
      rail.scrollBy({ top: direction * step, behavior: 'smooth' });
    };

    const handleIconClick = (option: SidebarOption) => {
      // If clicking the same option while expanded, collapse the panel and
      // clear the active tab so no rail icon remains highlighted.
      if (isExpanded && sidebarOptions === option) {
        videoCreator.collapseSidebar();
        return;
      }

      // After a collapse, `sidebarOptions` is `none`, so compare against the
      // panel we collapsed from — reopening that same tab restores where the
      // user left off (e.g. primary video settings, or the selected name tag's
      // Graphics sub-tab) instead of starting fresh.
      const effectiveCurrentOption =
        sidebarOptions === SidebarOption.none
          ? videoCreator.lastExpandedSidebarOption
          : sidebarOptions;

      // Handle special cases
      if (option === SidebarOption.karaoke) {
        videoCreator.setShowSubtitles(true);
        // When opening the captions panel and a caption template is already
        // applied, land on the styles/configuration view instead of the
        // template picker (templates is the default for the not-yet-generated
        // path). Without this, expanding the panel after the user has already
        // chosen a template sends them back to the picker.
        if (karaokeStore.defaultKaraokeConfig && karaokeStore.hasKaraokeLines) {
          videoCreator.karaokeSubMenu = KaraokeCard.styles;
        }
      }

      if (
        option === SidebarOption.aiProducer &&
        creatorStudioAiProducerEnableClipProducerOnly
      ) {
        videoCreator.aiProducerSubMenu = AIProducerCard.clip;
      }

      if (option === SidebarOption.design) {
        if (creatorStudioEnableDesignTabs) {
          // Nothing selected: the remembered sub-tab may have nothing to show.
          if (
            effectiveCurrentOption !== SidebarOption.design ||
            !activeElement
          ) {
            videoCreator.designSubMenu = DesignCard.text;
          }
        } else {
          if (
            effectiveCurrentOption !== SidebarOption.design &&
            (activeElement?.source.type !== 'text' ||
              activeElement?.source.track === KARAOKE_TRACK_NUMBER)
          ) {
            videoCreator.textProducer.setDefaultTextConfig();
          }
        }
      }

      videoCreator.sidebarOptions = option;
      // Don't clear active elements when expanding a panel that was set by element selection
      // (e.g., clicking Captions icon after selecting a caption, or Media after selecting a logo).
      if (effectiveCurrentOption !== option) {
        videoCreator.setActiveElements();
      }

      // Expand the panel if it's not already expanded
      if (!isExpanded) {
        onToggleExpand();
      }
    };

    return (
      <Rail>
        <Container ref={railRef} onScroll={syncScrollEdges}>
          {creatorStudioAiEnableTab && (
            <IconButton
              isSelected={isProducer && isExpanded}
              onClick={() => handleIconClick(SidebarOption.aiProducer)}
              aria-label="AI Producer"
            >
              <AiProducerIcon
                strokeColor={
                  isProducer && isExpanded
                    ? 'var(--accent)'
                    : 'var(--text-muted)'
                }
              />
              <IconLabel isSelected={isProducer && isExpanded}>
                AIProducer
              </IconLabel>
            </IconButton>
          )}

          {creatorStudioEnableVideoTemplatesTab && (
            <IconButton
              isSelected={isVideoTemplate && isExpanded}
              onClick={() => handleIconClick(SidebarOption.videoTemplate)}
              disabled={!isVideoReady}
              aria-label="Templates"
              aria-disabled={!isVideoReady}
            >
              <GridIcon
                width="20"
                height="20"
                strokeColor={
                  isVideoTemplate && isExpanded
                    ? 'var(--accent)'
                    : 'var(--text-muted)'
                }
              />
              <IconLabel isSelected={isVideoTemplate && isExpanded}>
                Templates
              </IconLabel>
            </IconButton>
          )}

          <IconButton
            isSelected={isKaraoke}
            onClick={() => handleIconClick(SidebarOption.karaoke)}
            disabled={!isVideoReady}
            aria-label="Captions"
            aria-disabled={!isVideoReady}
          >
            {/* PLAT-11877 — the captions glyph is the CAP bubble, as on the
                transcript's view switch; the microphone read as dubbing. */}
            <CaptionsBubbleIcon
              width="24"
              height="19"
              strokeColor={isKaraoke ? 'var(--accent)' : 'var(--text-muted)'}
            />
            <IconLabel isSelected={isKaraoke}>Captions</IconLabel>
          </IconButton>

          <IconButton
            isSelected={isSpeakers && isExpanded}
            onClick={() => handleIconClick(SidebarOption.speakers)}
            disabled={!isVideoReady}
            aria-label="Speakers"
            aria-disabled={!isVideoReady}
          >
            <SpeakersIcon
              strokeColor={
                isSpeakers && isExpanded ? 'var(--accent)' : 'var(--text-muted)'
              }
            />
            <IconLabel isSelected={isSpeakers && isExpanded}>
              Speakers
            </IconLabel>
          </IconButton>

          {creatorStudioMediaEnableTab && (
            <IconButton
              isSelected={isMedia}
              onClick={() => handleIconClick(SidebarOption.media)}
              aria-label="Media"
            >
              <MediaIcon
                strokeColor={isMedia ? 'var(--accent)' : 'var(--text-muted)'}
              />
              <IconLabel isSelected={isMedia}>Media</IconLabel>
            </IconButton>
          )}

          <IconButton
            isSelected={isDesign}
            onClick={() => handleIconClick(SidebarOption.design)}
            disabled={!isVideoReady}
            aria-label="Graphics"
            aria-disabled={!isVideoReady}
          >
            <TextIcon
              height="20"
              width="20"
              fillColor={isDesign ? 'var(--accent)' : 'var(--text-muted)'}
            />
            <IconLabel isSelected={isDesign}>Graphics</IconLabel>
          </IconButton>

          <IconButton
            isSelected={isHistory && isExpanded}
            onClick={() => handleIconClick(SidebarOption.history)}
            aria-label="History"
          >
            <HistoryIcon
              height="20"
              width="20"
              strokeColor={
                isHistory && isExpanded ? 'var(--accent)' : 'var(--text-muted)'
              }
            />
            <IconLabel isSelected={isHistory && isExpanded}>History</IconLabel>
          </IconButton>
        </Container>
        <ScrollEdgeFade $side="top" $visible={railEdges.top} />
        <ScrollEdgeFade $side="bottom" $visible={railEdges.bottom} />
        {railEdges.top && (
          <ScrollCue
            $side="top"
            onClick={() => scrollRailBy(-1)}
            aria-label="Scroll tabs up"
          >
            <ChevronDownIcon width="12" height="12" strokeWidth="2" />
          </ScrollCue>
        )}
        {railEdges.bottom && (
          <ScrollCue
            $side="bottom"
            onClick={() => scrollRailBy(1)}
            aria-label="Scroll tabs down"
          >
            <ChevronDownIcon width="12" height="12" strokeWidth="2" />
          </ScrollCue>
        )}
      </Rail>
    );
  },
);

export default SidebarIconMenu;

// The rail's outer box: owns the chrome (background, border, width) and anchors
// the edge fades, so they stay pinned to the fold while the list scrolls under
// them.
/** The rail's in-flow width; layout math elsewhere reads this. */
export const RAIL_MIN_WIDTH = 64;

const Rail = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background-color: var(--background);
  min-width: ${RAIL_MIN_WIDTH}px;
`;

// The scrolling list of tabs. `min-height: 0` lets it shrink below its content
// so it can scroll rather than overflow the editor area. The scrollbar is
// hidden because the rail is only ~64px wide — reserving a gutter for it would
// change the rail's width the moment it starts scrolling; the edge fades are
// the affordance instead.
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 8px;
  gap: 8px;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

// The "there's more this way" control, sat on top of the edge fade. Rendered
// only while that edge actually has tabs behind it, so it never points at
// nothing — and it scrolls on click rather than merely reporting that it could.
//
// It is a raised, opaque pill rather than a bare glyph on purpose. A plain
// chevron landed on whichever tab icon was peeking at the fold (History's
// circular arrow, most often) and the two shapes merged into one — it read as a
// seventh tab rather than a control. The solid fill covers whatever is behind
// it, and the gold chevron borrows the disclosure language the Hide Timeline
// control already uses, so it cannot be mistaken for a grey tab glyph.
const ScrollCue = styled.button<{ $side: 'top' | 'bottom' }>`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  ${props => (props.$side === 'top' ? 'top: 6px;' : 'bottom: 6px;')}
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 1px solid var(--border);
  border-radius: 50%;
  background-color: var(--surface-elevated);
  box-shadow: var(--elevation-sm);
  color: var(--accent);
  cursor: pointer;
  transition:
    background-color 200ms ease,
    border-color 200ms ease;

  ${props =>
    props.$side === 'top' &&
    css`
      svg {
        transform: rotate(180deg);
      }
    `}

  &:hover {
    background-color: var(--surface-elevated-hover);
    border-color: var(--accent);
  }
`;

const IconButton = styled.button<{ isSelected?: boolean; disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px;
  border: none;
  background: transparent;
  border-radius: var(--radius);
  cursor: pointer;
  transition: all 200ms ease;
  width: 100%;
  /* Keep full height inside the scrolling rail — without this the tabs
     compress to fit a short viewport instead of scrolling. */
  flex-shrink: 0;

  svg {
    color: var(--text-muted);
    transition: color 200ms ease;
  }

  &:hover {
    background-color: var(--surface-elevated);

    svg {
      color: var(--foreground);
    }
  }

  &:active {
    background-color: var(--gray-700);
    scale: 0.98;
  }

  ${props =>
    props.isSelected &&
    css`
      background-color: var(--surface-elevated);

      svg {
        color: var(--accent);
      }
    `}

  ${props =>
    props.disabled &&
    css`
      pointer-events: none;
      opacity: 0.5;
      cursor: not-allowed;
    `}
`;

const IconLabel = styled.span<{ isSelected?: boolean }>`
  font-size: var(--text-xs);
  color: ${props =>
    props.isSelected ? 'var(--accent)' : 'var(--text-secondary)'};
  transition: color 200ms ease;
  white-space: nowrap;

  ${IconButton}:hover & {
    color: var(--foreground);
  }
`;
